import { z } from 'zod';

export const WEBSITE_THEME_VERSION = '2026-09-12' as const;
export const WEBSITE_THEME_ASSIGNMENT_KEY = 'botspot:website-theme-cohort:2026-09-12';
export const WEBSITE_THEME_FAMILIES = [
  'home',
  'bootcamp',
  'self-paced',
  'challenge',
  'pricing',
  'signup',
  'google-search',
  'marketplace',
  'checkout',
] as const;
export type WebsiteThemeFamily = (typeof WEBSITE_THEME_FAMILIES)[number];
export type WebsiteThemeVariant = 'control' | 'test';
export interface WebsiteThemeAttribution {
  posthog_distinct_id: string;
  theme_experiment_key: string;
  theme_experiment_variant: WebsiteThemeVariant;
  theme_experiment_entry_page: WebsiteThemeFamily;
  theme_experiment_version: typeof WEBSITE_THEME_VERSION;
  /** Provider definition revision, used only to distinguish identity completion from a rollout edit. */
  theme_experiment_flag_version?: string;
}
let memoryAssignment: WebsiteThemeAttribution | null = null;
export function websiteThemeFlag(family: WebsiteThemeFamily): string {
  return `website-theme-${family}-${WEBSITE_THEME_VERSION}`;
}
export function getWebsiteThemeEntryPage(pathname: string): WebsiteThemeFamily | null {
  const path = pathname.replace(/\/$/, '') || '/';
  if (path === '/' || path === '/index.html') return 'home';
  if (['/courses/ai-trading-bootcamp/self-paced'].includes(path)) return 'self-paced';
  if (['/courses/ai-trading-bootcamp'].includes(path)) return 'bootcamp';
  if (path === '/challenges') return 'challenge';
  if (path === '/pricing') return 'pricing';
  if (
    [
      '/sales',
      '/sales-ai-agent-entry',
      '/sales-choose',
      '/sales-marketplace',
      '/sales-ai-prompt',
      '/choose-plan',
    ].includes(path)
  )
    return 'signup';
  if (path === '/lp/google-search') return 'google-search';
  if (path === '/marketplace' || /^\/marketplace\/(strategy|account)\/[^/]+$/.test(path))
    return 'marketplace';
  if (['/checkout', '/checkout/starter-plan', '/checkout/light-plan'].includes(path))
    return 'checkout';
  return null;
}
export function isWebsiteThemeTrafficEligible(hostname: string, excluded = false): boolean {
  return (
    !excluded &&
    ['botspot.trade', 'www.botspot.trade'].includes(hostname) &&
    !navigator.webdriver &&
    !/HeadlessChrome|Playwright|Puppeteer|Chrome-Lighthouse|bot|crawler|spider/i.test(
      navigator.userAgent,
    )
  );
}
const assignmentSchema = z
  .object({
    posthog_distinct_id: z.string().min(1),
    theme_experiment_key: z.string(),
    theme_experiment_variant: z.enum(['control', 'test']),
    theme_experiment_entry_page: z.enum(WEBSITE_THEME_FAMILIES),
    theme_experiment_version: z.literal(WEBSITE_THEME_VERSION),
    theme_experiment_flag_version: z.string().min(1).optional(),
  })
  .refine(
    (assignment) =>
      assignment.theme_experiment_key === websiteThemeFlag(assignment.theme_experiment_entry_page),
  );
export function getWebsiteThemeAttribution(): WebsiteThemeAttribution | null {
  try {
    const raw = localStorage.getItem(WEBSITE_THEME_ASSIGNMENT_KEY);
    if (!raw) return memoryAssignment;
    const parsed = assignmentSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return memoryAssignment ? { ...memoryAssignment } : null;
  }
}
export function persistWebsiteThemeAssignment(assignment: WebsiteThemeAttribution): void {
  try {
    localStorage.setItem(WEBSITE_THEME_ASSIGNMENT_KEY, JSON.stringify(assignment));
    memoryAssignment = null;
  } catch {
    memoryAssignment = { ...assignment };
  }
}

/** Keep the first applied cohort through identity completion, but let current
 * provider state win when its definition changed or no revision was recorded. */
export function resolveWebsiteThemeAssignment(
  candidate: WebsiteThemeAttribution,
  previous: WebsiteThemeAttribution | null,
): WebsiteThemeAttribution {
  if (!previous) return candidate;
  if (previous.theme_experiment_key !== candidate.theme_experiment_key) return previous;
  if (previous.theme_experiment_variant === candidate.theme_experiment_variant) {
    if (
      candidate.theme_experiment_flag_version !== undefined &&
      candidate.theme_experiment_flag_version !== previous.theme_experiment_flag_version
    ) {
      return {
        ...previous,
        theme_experiment_flag_version: candidate.theme_experiment_flag_version,
      };
    }
    return previous;
  }
  if (
    previous.posthog_distinct_id !== candidate.posthog_distinct_id &&
    previous.theme_experiment_flag_version !== undefined &&
    previous.theme_experiment_flag_version === candidate.theme_experiment_flag_version
  ) {
    return previous;
  }
  return candidate;
}

/** First applied entry owns the flag family across tabs. Recheck continuity in
 * the same atomic claim that establishes the first painted cohort. */
export async function claimWebsiteThemeAssignment(
  candidate: WebsiteThemeAttribution,
  canClaim: () => boolean = () => true,
): Promise<WebsiteThemeAttribution | null> {
  const claim = () => {
    if (!canClaim()) return null;
    const winner = getWebsiteThemeAttribution();
    const assignment = resolveWebsiteThemeAssignment(candidate, winner);
    if (assignment !== winner) persistWebsiteThemeAssignment(assignment);
    return assignment;
  };
  if (navigator.locks?.request) {
    try {
      return await navigator.locks.request(WEBSITE_THEME_ASSIGNMENT_KEY, claim);
    } catch {
      /* Unsupported lock access uses the storage recheck path. */
    }
  }
  if (!claim()) return null;
  await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
  return getWebsiteThemeAttribution() || candidate;
}
